/**
 * Single source of truth for 4" x 6" postcard print specs (Lob).
 *
 * Lob prints 4x6 postcards from full-bleed HTML at 300 DPI:
 * - Bleed canvas: 6.25" x 4.25" = 1875px x 1275px
 * - Trim (finished) size: 6" x 4" = 1800px x 1200px (0.125" bleed trimmed off each side)
 * - Safe zone: all critical content must sit 0.25" inside the bleed edges
 *   (0.125" inside the trim line), per Lob's "1/8 inch from the edge of the
 *   final size" rule.
 *
 * Keep-clear blocks (postage, barcode, address) are anchored to the TRIM
 * (finished card) edges, never the bleed edges: anything outside trim is cut
 * off in production, so a keep-clear zone extending past the red trim line
 * would be meaningless. They intentionally cross the green safe line — safe
 * governs *your* content, keep-clear blocks mark printer-reserved areas.
 * Sources: Lob Help Center "Artboard layout" + "Postcards" design specs,
 * lob/examples 4x6-back.html, USPS DMM 202.5 (barcode clear zone).
 * Dashboard templates are full-bleed HTML at 1875 x 1275px (300 DPI).
 * Custom HTML authored at the old 888 x 600px size is detected via
 * detectPostcardDimensions() so the editor can warn authors to reset.
 */

export const POSTCARD_PRINT_DPI = 300

/** Full-bleed canvas size in inches (includes 0.125" bleed on all sides). */
export const POSTCARD_BLEED_WIDTH_IN = 6.25
export const POSTCARD_BLEED_HEIGHT_IN = 4.25

/** Finished (trimmed) postcard size in inches. */
export const POSTCARD_TRIM_WIDTH_IN = 6
export const POSTCARD_TRIM_HEIGHT_IN = 4

/** Bleed inset: distance from bleed edge to trim line. */
export const POSTCARD_TRIM_INSET_IN = 0.125

/** Safe-zone inset: distance from bleed edge to safe zone. */
export const POSTCARD_SAFE_INSET_IN = 0.25

/** Full-bleed canvas at 300 DPI. */
export const POSTCARD_BLEED_WIDTH_PX = 1875
export const POSTCARD_BLEED_HEIGHT_PX = 1275

/** Pixel insets at 300 DPI. */
export const POSTCARD_TRIM_INSET_PX = 37.5
export const POSTCARD_SAFE_INSET_PX = 75

/**
 * Lob address/postage reservation block (back side only).
 * Dimensions: 985px x 713px (3.2833" x 2.3767" at 300 DPI).
 * Anchored to the bottom-right of the bleed canvas:
 *   x = CanvasWidth - 1068px, y = CanvasHeight - 788px
 * Which leaves a 83px (0.2767") right offset and 75px (0.25") bottom offset.
 */
export const POSTCARD_ADDRESS_BLOCK_WIDTH_PX = 985
export const POSTCARD_ADDRESS_BLOCK_HEIGHT_PX = 713
export const POSTCARD_ADDRESS_BLOCK_RIGHT_OFFSET_PX = 83
export const POSTCARD_ADDRESS_BLOCK_BOTTOM_OFFSET_PX = 75
export const POSTCARD_ADDRESS_BLOCK_X_FROM_RIGHT_PX = 1068
export const POSTCARD_ADDRESS_BLOCK_Y_FROM_BOTTOM_PX = 788

/**
 * Postage keep-clear area (back side only): 1.5" x 1.5" block anchored to
 * the top-right corner of the TRIM (finished card), i.e. 0.125" inside the
 * bleed edges. Lob prints the actual indicia inside its address block, so
 * this is a conservative keep-clear for custom designs, not a Lob mandate.
 */
export const POSTCARD_POSTAGE_WIDTH_IN = 1.5
export const POSTCARD_POSTAGE_HEIGHT_IN = 1.5

/**
 * USPS barcode keep-clear area (back side only) per DMM 202.5: the barcode
 * clear zone is the lower-right of the address side, 4.75" wide measured
 * from the right edge of the piece with its top 5/8" from the bottom edge
 * of the piece. "Piece" = finished card, so the zone is anchored 0.125"
 * inside the bleed edges (on the trim lines), NOT full-bleed-width.
 */
export const POSTCARD_BARCODE_WIDTH_IN = 4.75
export const POSTCARD_BARCODE_HEIGHT_IN = 0.625

/**
 * Dashboard template/preview size in px (full-bleed 300 DPI canvas).
 * Preview lockdown CSS and iframe scaling use this.
 */
export const POSTCARD_PREVIEW_WIDTH_PX = 1875
export const POSTCARD_PREVIEW_HEIGHT_PX = 1275

export const PRINT_GUIDE_COLORS = {
    trim: '#EF4444',
    safe: '#10B981',
    addressFill: 'rgba(239, 68, 68, 0.2)',
    addressBorder: '#EF4444',
    postageFill: 'rgba(34, 211, 238, 0.18)',
    postageBorder: '#06B6D4',
    barcodeFill: 'rgba(250, 204, 21, 0.18)',
    barcodeBorder: '#CA8A04',
} as const

export const PRINT_GUIDE_ADDRESS_LABEL = 'Reserved for Postage & Address'
export const PRINT_GUIDE_POSTAGE_LABEL = 'Postage Area'
export const PRINT_GUIDE_BARCODE_LABEL = 'USPS Barcode Area'

/** DOM marker used by the overlay; asserted absent from exports in tests. */
export const PRINT_GUIDE_DATA_ATTR = 'data-print-guide'

function toPercent(inches: number, totalInches: number): string {
    return `${(inches / totalInches) * 100}%`
}

/**
 * Guide geometry as CSS percentages of the bleed canvas.
 * Percentages keep the overlay correct at any render resolution.
 */
export const POSTCARD_PRINT_GUIDES = {
    trim: {
        left: toPercent(
            POSTCARD_TRIM_INSET_IN,
            POSTCARD_BLEED_WIDTH_IN,
        ),
        top: toPercent(
            POSTCARD_TRIM_INSET_IN,
            POSTCARD_BLEED_HEIGHT_IN,
        ),
        width: toPercent(
            POSTCARD_BLEED_WIDTH_IN - POSTCARD_TRIM_INSET_IN * 2,
            POSTCARD_BLEED_WIDTH_IN,
        ),
        height: toPercent(
            POSTCARD_BLEED_HEIGHT_IN - POSTCARD_TRIM_INSET_IN * 2,
            POSTCARD_BLEED_HEIGHT_IN,
        ),
    },
    safe: {
        left: toPercent(
            POSTCARD_SAFE_INSET_IN,
            POSTCARD_BLEED_WIDTH_IN,
        ),
        top: toPercent(
            POSTCARD_SAFE_INSET_IN,
            POSTCARD_BLEED_HEIGHT_IN,
        ),
        width: toPercent(
            POSTCARD_BLEED_WIDTH_IN - POSTCARD_SAFE_INSET_IN * 2,
            POSTCARD_BLEED_WIDTH_IN,
        ),
        height: toPercent(
            POSTCARD_BLEED_HEIGHT_IN - POSTCARD_SAFE_INSET_IN * 2,
            POSTCARD_BLEED_HEIGHT_IN,
        ),
    },
    addressBlock: {
        width: toPercent(
            POSTCARD_ADDRESS_BLOCK_WIDTH_PX / POSTCARD_PRINT_DPI,
            POSTCARD_BLEED_WIDTH_IN,
        ),
        height: toPercent(
            POSTCARD_ADDRESS_BLOCK_HEIGHT_PX / POSTCARD_PRINT_DPI,
            POSTCARD_BLEED_HEIGHT_IN,
        ),
        right: toPercent(
            POSTCARD_ADDRESS_BLOCK_RIGHT_OFFSET_PX / POSTCARD_PRINT_DPI,
            POSTCARD_BLEED_WIDTH_IN,
        ),
        bottom: toPercent(
            POSTCARD_ADDRESS_BLOCK_BOTTOM_OFFSET_PX / POSTCARD_PRINT_DPI,
            POSTCARD_BLEED_HEIGHT_IN,
        ),
    },
    postage: {
        top: toPercent(
            POSTCARD_TRIM_INSET_IN,
            POSTCARD_BLEED_HEIGHT_IN,
        ),
        right: toPercent(
            POSTCARD_TRIM_INSET_IN,
            POSTCARD_BLEED_WIDTH_IN,
        ),
        width: toPercent(
            POSTCARD_POSTAGE_WIDTH_IN,
            POSTCARD_BLEED_WIDTH_IN,
        ),
        height: toPercent(
            POSTCARD_POSTAGE_HEIGHT_IN,
            POSTCARD_BLEED_HEIGHT_IN,
        ),
    },
    barcode: {
        bottom: toPercent(
            POSTCARD_TRIM_INSET_IN,
            POSTCARD_BLEED_HEIGHT_IN,
        ),
        right: toPercent(
            POSTCARD_TRIM_INSET_IN,
            POSTCARD_BLEED_WIDTH_IN,
        ),
        width: toPercent(
            POSTCARD_BARCODE_WIDTH_IN,
            POSTCARD_BLEED_WIDTH_IN,
        ),
        height: toPercent(
            POSTCARD_BARCODE_HEIGHT_IN,
            POSTCARD_BLEED_HEIGHT_IN,
        ),
    },
} as const

export interface PostcardCanvasDimensions {
    width: number
    height: number
}

function parsePxDimensions(
    cssBlock: string | undefined,
): PostcardCanvasDimensions | null {
    if (!cssBlock) return null
    const widthMatch = cssBlock.match(
        /width\s*:\s*(\d+(?:\.\d+)?)px/i,
    )
    const heightMatch = cssBlock.match(
        /height\s*:\s*(\d+(?:\.\d+)?)px/i,
    )
    if (!widthMatch?.[1] || !heightMatch?.[1]) return null
    return {
        width: Number(widthMatch[1]),
        height: Number(heightMatch[1]),
    }
}

/**
 * Best-effort parse of the postcard canvas size from user-authored HTML.
 * Prefers the `.page` rule, falls back to `body`. Returns null when no
 * explicit px dimensions are found (e.g. inch-based or responsive layouts).
 */
export function detectPostcardDimensions(
    html: string,
): PostcardCanvasDimensions | null {
    const pageBlock = html.match(/\.page\s*\{([^}]*)\}/i)?.[1]
    const fromPage = parsePxDimensions(pageBlock)
    if (fromPage) return fromPage

    const bodyBlock = html.match(/body\s*\{([^}]*)\}/i)?.[1]
    return parsePxDimensions(bodyBlock)
}

/** Expected template size: full-bleed 1875x1275 (old 888x600 custom HTML warns). */
export function isExpectedPostcardSize(
    dims: PostcardCanvasDimensions | null,
): boolean {
    if (!dims) return true
    return (
        dims.width === POSTCARD_PREVIEW_WIDTH_PX &&
        dims.height === POSTCARD_PREVIEW_HEIGHT_PX
    )
}
